import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError, setTokens, clearTokens } from '@/lib/api';
import { useAuthStore, type CurrentUser } from '@/stores/auth';
import { useScopePinStore } from '@/stores/scope-pin';
import { toast } from '@/stores/toast';

type SwitchTenantResponse = {
  access_token: string;
  refresh_token: string;
  active_tenant_id: string;
  edition: 'lite' | 'full';
};

/** テナント切替の共通処理 (v1.0 Phase4、docs/design/v10-phase4-platform-admin-ui.md §3-2)。
 *  TenantSwitcher (他テナントへの切替) と SystemAdminBanner (自分のテナントへ戻る) の両方から使う。 */
export function useSwitchTenant() {
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);

  const switchTenant = async (tenantId: string, successLabel: string) => {
    setSwitching(true);
    try {
      const res = await apiFetch<SwitchTenantResponse>('/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      setTokens(res.access_token, res.refresh_token);
      try {
        // QAレビューHIGH反映: clear()前にin-flightのクエリをcancelしないと、旧テナント宛の
        // リクエストがclear()後に解決してキャッシュへ書き戻り、新テナント画面に旧テナントの
        // データが一瞬混入しうる
        await queryClient.cancelQueries();
        // main.tsxのregisterAuthClearedHandlerと同じ効果 (Zustandユーザ状態+TanStack Queryキャッシュの
        // 全破棄) を明示的に呼ぶ。401エラー経路ではないため既存ハンドラは流用しない
        queryClient.clear();
        // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-2②③): scope.tsのselectedNodeIdは
        // /nodes再取得完了(NodeSwitcherのsetTree)まで旧テナント値が残る非同期復元だが、
        // scope-pin.tsは/nodes待ちが不要なため、ここで同期的に新テナントの固定状態へ切り替える
        // (新テナントの固定範囲と旧テナントのnodeIdが混在する窓を最小化する、platform-reviewer指摘)
        useScopePinStore.getState().loadForTenant(res.active_tenant_id);
        const me = await apiFetch<CurrentUser>('/auth/me');
        setUser(me);
      } catch (meErr) {
        // QAレビューHIGH反映: setTokens済みだが/auth/me失敗という中間状態(トークンは新テナント、
        // Zustandのuserは旧テナントのまま)を放置すると、以降のAPI呼出が新テナント向けなのに
        // 画面表示は旧テナントのままという不整合になる。安全側に倒し、強制ログアウトへ倒す
        clearTokens();
        setUser(null);
        navigate('/login', { replace: true });
        throw meErr;
      }
      toast.success(`${successLabel}に切り替えました`);
      navigate('/', { replace: true });
      return true;
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '切替に失敗しました。再度ログインしてください');
      return false;
    } finally {
      setSwitching(false);
    }
  };

  return { switchTenant, switching };
}
